import React, { useState } from "react";
import PropTypes from "prop-types";

import { Modal, Button, Icon } from "semantic-ui-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft, faRotateRight, faPrint } from "@fortawesome/free-solid-svg-icons";
import Lightbox from "yet-another-react-lightbox";
import { Fullscreen, Zoom, Download } from "yet-another-react-lightbox/plugins";
import "yet-another-react-lightbox/styles.css";

import styles from "./styles.module.css";

const PREVIEW_FIELDS = 3;

const humanizeKey = (key) => {
	if (key === null || key === undefined) return "";
	return String(key)
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replace(/[_-]+/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.replace(/^\w/, (c) => {
			return c.toUpperCase();
		});
};

const getFieldLabel = (field) => {
	return field?.label || humanizeKey(field?.name);
};

const escapeHtml = (value) => {
	return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
};

const formatDate = (value) => {
	const str = String(value);
	const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (m) return `${m[3]}/${m[2]}/${m[1]}`;
	return str;
};

const getOptionLabel = (field, value) => {
	const opt = (field?.options || []).find((o) => {
		return String(o.value) === String(value);
	});
	return opt ? opt.label : value;
};

const formatFieldValue = (field) => {
	const value = field?.value;
	if (value === null || value === undefined || value === "") return "";
	const widget = String(field?.widget || "").toLowerCase();
	if (["radio", "dropdown", "select"].includes(widget)) {
		const label = getOptionLabel(field, value);
		if (label === null || label === undefined) return String(value);
		return String(label);
	}
	if (widget === "checkbox") {
		return String(value).toLowerCase() === "true" || value === true ? "Sí" : "No";
	}
	if (widget === "date") return formatDate(value);
	if (typeof value === "boolean") return value ? "Sí" : "No";
	if (typeof value === "object") {
		try {
			const json = JSON.stringify(value);
			return json && json !== "{}" && json !== "[]" ? json : "";
		} catch (e) {
			return String(value);
		}
	}
	return String(value);
};

const groupByBlock = (fields) => {
	const map = new Map();
	fields.forEach((f) => {
		const block = f?.block || "Información General";
		if (!map.has(block)) map.set(block, []);
		map.get(block).push(f);
	});
	return Array.from(map, ([title, list]) => {
		return { title, fields: list };
	});
};

const getDocValue = (doc) => {
	let value = "";
	if (doc?.value !== null && doc?.value !== undefined) {
		value = doc.value;
	} else if (doc?.url !== null && doc?.url !== undefined) {
		value = doc.url;
	} else if (doc?.src !== null && doc?.src !== undefined) {
		value = doc.src;
	} else if (doc?.uri !== null && doc?.uri !== undefined) {
		value = doc.uri;
	}
	if (typeof value === "object") {
		value = value.url || value.src || value.uri || value.link || value.file || JSON.stringify(value);
	}
	value = String(value);
	if (/^(https?:|blob:|file:|data:)/i.test(value)) return value;
	const base = String(import.meta.env.VITE_API_URL || "").replace(/\/+$/, "");
	return `${base}/downloads/${value.replace(/\\/g, "/")}`;
};

const getDocName = (doc) => {
	const url = getDocValue(doc);
	const clean = url.split("?")[0].split("#")[0];
	const parts = clean.split("/");
	const basename = parts[parts.length - 1] || "";
	if (basename && basename.includes(".")) return basename;
	const label = doc?.label || doc?.name || "";
	return label || basename || "Documento adjunto";
};

const isImageDoc = (doc) => {
	const widget = String(doc?.widget || "").toLowerCase();
	if (widget === "image" || widget === "signature") return true;
	return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(getDocValue(doc));
};

const isPdfDoc = (doc) => {
	const widget = String(doc?.widget || "").toLowerCase();
	if (widget === "pdf") return true;
	return /\.pdf(\?|#|$)/i.test(getDocValue(doc));
};

const buildPrintHtml = (formName, flowId, fields, documents) => {
	const sections = groupByBlock(fields);
	const now = new Date();
	const dateStr = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

	const sectionsHtml = sections
		.map((sec) => {
			const rowsHtml = sec.fields
				.map((f) => {
					return `<tr>
						<td class="flow-label">${escapeHtml(getFieldLabel(f))}</td>
						<td class="flow-colon">:</td>
						<td class="flow-data">${escapeHtml(formatFieldValue(f))}</td>
					</tr>`;
				})
				.join("");
			return `<div class="flow-block">
				<div class="flow-block-title">${escapeHtml(sec.title)}</div>
				<table class="flow-table">${rowsHtml}</table>
			</div>`;
		})
		.join("");

	const docsHtml = documents.length
		? documents
				.map((d) => {
					return `<div class="flow-doc">• ${escapeHtml(getDocName(d))}</div>`;
				})
				.join("")
		: `<div class="flow-doc">No hay documentos adjuntos.</div>`;

	const imagePagesHtml = documents
		.filter((d) => {
			return isImageDoc(d);
		})
		.map((d) => {
			return `<div class="flow-image-page">
				<div class="flow-image-title">${escapeHtml(getDocName(d))}</div>
				<img class="flow-image" src="${escapeHtml(getDocValue(d))}" alt="${escapeHtml(getDocName(d))}" />
			</div>`;
		})
		.join("");

	return `<!DOCTYPE html>
<html>
<head>
    <title>${escapeHtml(formName)}</title>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 30px 40px; background: #fff; margin: 0; color: #1a1a1a; }
        .flow-print-header { text-align: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #184788; }
        .flow-print-title { font-size: 20px; font-weight: 700; color: #184788; }
        .flow-print-meta { font-size: 11px; color: #6c757d; margin-top: 6px; }
        .flow-block { margin-bottom: 24px; }
        .flow-block-title { font-size: 13px; color: #184788; border-bottom: 2px solid #e8eef6; padding-bottom: 6px; margin-bottom: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; }
        .flow-table { width: 100%; border-collapse: collapse; }
        .flow-table tr { border-bottom: 1px solid #f0f0f0; }
        .flow-table tr:last-child { border-bottom: none; }
        .flow-table td { padding: 6px 8px; vertical-align: top; font-size: 13px; }
        .flow-label { width: 34%; color: #3f3f46; font-weight: 600; }
        .flow-colon { width: 20px; text-align: center; color: #999; }
        .flow-data { color: #6b7280; word-break: break-word; white-space: pre-wrap; }
        .flow-doc { font-size: 13px; color: #6b7280; padding: 3px 0; }
        .flow-image-page { page-break-before: always; break-before: page; text-align: center; margin-top: 40px; }
        .flow-image-title { font-size: 12px; color: #6c757d; margin-bottom: 12px; }
        .flow-image { max-width: 100%; max-height: calc(100vh - 120px); object-fit: contain; }
    </style>
</head>
<body>
    <div class="flow-print-header">
       <div class="flow-print-title">${escapeHtml(formName)}</div>
        <div class="flow-print-meta">Impreso: ${dateStr}</div>
    </div>
    ${sectionsHtml}
    <div class="flow-block">
        <div class="flow-block-title">Documentos Adjuntos</div>
        ${docsHtml}
    </div>
    ${imagePagesHtml}
</body>
</html>`;
};

const openPrintWindow = (formName, flowId, fields, documents) => {
	const win = window.open("", "_blank");
	if (!win) {
		return;
	}
	win.document.write(buildPrintHtml(formName, flowId, fields, documents));
	win.document.close();

	const images = Array.from(win.document.querySelectorAll("img.flow-image"));
	let finished = false;
	const finish = () => {
		if (finished) {
			return;
		}
		finished = true;
		win.focus();
		win.print();
		win.close();
	};

	if (!images.length) {
		setTimeout(finish, 200);
		return;
	}

	let loaded = 0;
	images.forEach((img) => {
		const onDone = () => {
			loaded += 1;
			if (loaded >= images.length) {
				setTimeout(finish, 150);
			}
		};
		if (img.complete) {
			onDone();
		} else {
			img.addEventListener("load", onDone);
			img.addEventListener("error", onDone);
		}
	});
	setTimeout(finish, 8000);
};

const DocViewer = ({ doc, onClose }) => {
	const url = getDocValue(doc);
	const name = getDocName(doc);
	const [rotationAngle, setRotationAngle] = useState(0);

	const handleRotateLeft = () => {
		setRotationAngle((prev) => {
			return prev <= 0 ? 270 : prev - 90;
		});
	};

	const handleRotateRight = () => {
		setRotationAngle((prev) => {
			return prev >= 270 ? 0 : prev + 90;
		});
	};

	const handleCloseLightbox = () => {
		setRotationAngle(0);
		onClose();
	};

	const handlePrint = () => {
		window.print();
	};

	if (isImageDoc(doc)) {
		return (
			<Lightbox
				open
				plugins={[Fullscreen, Download, Zoom]}
				close={handleCloseLightbox}
				slides={[{ src: url }]}
				zoom={{
					maxZoomPixelRatio: 5,
					zoomInMultiplier: 2,
				}}
				className={`rotated${rotationAngle}`}
				toolbar={{
					buttons: [
						<button key="print" type="button" className="yarl__button" onClick={handlePrint}>
							<FontAwesomeIcon icon={faPrint} size="xl" />
						</button>,
						<button key="rotate-left" type="button" className="yarl__button" onClick={handleRotateLeft}>
							<FontAwesomeIcon icon={faRotateLeft} size="xl" />
						</button>,
						<button key="rotate-right" type="button" className="yarl__button" onClick={handleRotateRight}>
							<FontAwesomeIcon icon={faRotateRight} size="xl" />
						</button>,
						"zoom",
						"download",
						"fullscreen",
						"close",
					],
				}}
			/>
		);
	}

	return (
		<Modal open onClose={onClose} size="large" closeIcon centered className={styles.viewerModal}>
			<Modal.Header className={styles.viewerHeader}>
				<span className={styles.viewerHeaderIcon}>
					<Icon name="file" />
				</span>
				<span className={styles.viewerTitle}>{name}</span>
			</Modal.Header>
			<Modal.Content scrolling className={styles.viewerContent}>
				{isPdfDoc(doc) ? (
					<iframe title={name} src={url} className={styles.viewerFrame} />
				) : (
					<div className={styles.viewerGeneric}>
						<Icon name="file outline" size="huge" />
						<span>{name}</span>
					</div>
				)}
			</Modal.Content>
			<Modal.Actions className={styles.modalActions}>
				<Button onClick={onClose} className={styles.btnClose}>
					Cerrar
				</Button>
				<a href={url} target="_blank" rel="noreferrer" download={name} className={styles.downloadLink}>
					<Button className={styles.btnPrint}>
						<Icon name="download" /> Descargar
					</Button>
				</a>
			</Modal.Actions>
		</Modal>
	);
};

DocViewer.propTypes = {
	doc: PropTypes.object,
	onClose: PropTypes.func,
};

const DocThumbnail = ({ doc, onView }) => {
	const url = getDocValue(doc);
	const name = getDocName(doc);
	return (
		<button type="button" className={styles.docItem} onClick={onView}>
			<span className={styles.docThumb}>
				{isImageDoc(doc) ? (
					<img src={url} alt={name} />
				) : isPdfDoc(doc) ? (
					<Icon name="file pdf outline" size="large" className={styles.docThumbIcon} />
				) : (
					<Icon name="file outline" size="large" className={styles.docThumbIcon} />
				)}
			</span>
			<span className={styles.docName}>{name}</span>
		</button>
	);
};

DocThumbnail.propTypes = {
	doc: PropTypes.object,
	onView: PropTypes.func,
};

const FlowFormResponseModal = ({ formName, flowId, fields, documents, onClose }) => {
	const [viewerDoc, setViewerDoc] = useState(null);
	const sections = groupByBlock(fields);

	const handlePrint = () => {
		openPrintWindow(formName, flowId, fields, documents);
	};

	return (
		<Modal open onClose={onClose} size="large" closeIcon centered scrolling className={styles.modal}>
			<Modal.Header className={styles.modalHeader}>
				<div className={styles.modalHeaderInner}>
					<span className={styles.modalHeaderIcon}>
						<Icon name="file text outline" />
					</span>
					<span className={styles.modalTitle}>{formName}</span>
					{/* {flowId && <span className={styles.modalId}>ID: {flowId}</span>} */}
				</div>
			</Modal.Header>
			<Modal.Content scrolling className={styles.modalContent}>
				{sections.map((sec) => {
					return (
						<section key={sec.title} className={styles.section}>
							<div className={styles.sectionHeader}>
								<Icon name="file text outline" className={styles.sectionIcon} />
								<h3 className={styles.sectionTitle}>{sec.title}</h3>
							</div>
							<div className={styles.sectionLine} />
							<div className={styles.sectionFields}>
								{sec.fields.map((f, i) => {
									return (
										<div key={i} className={styles.fieldRow}>
											<span className={styles.fieldLabel}>{getFieldLabel(f)}</span>
											<span className={styles.fieldColon}>:</span>
											<span className={styles.fieldValue}>{formatFieldValue(f)}</span>
										</div>
									);
								})}
							</div>
						</section>
					);
				})}
				<section className={styles.section}>
					<div className={styles.sectionHeader}>
						<Icon name="file image outline" className={styles.sectionIcon} />
						<h3 className={styles.sectionTitle}>Documentos Adjuntos</h3>
					</div>
					<div className={styles.sectionLine} />
					{documents.length ? (
						<div className={styles.docGrid}>
							{documents.map((doc, i) => {
								return (
									<DocThumbnail
										key={i}
										doc={doc}
										onView={() => {
											return setViewerDoc(doc);
										}}
									/>
								);
							})}
						</div>
					) : (
						<div className={styles.docEmpty}>
							<Icon name="file outline" />
							<span>No hay documentos adjuntos.</span>
						</div>
					)}
				</section>
			</Modal.Content>
			<Modal.Actions className={styles.modalActions}>
				<Button onClick={onClose} className={styles.btnClose}>
					Cerrar
				</Button>
				<Button onClick={handlePrint} className={styles.btnPrint}>
					<Icon name="print" /> Imprimir
				</Button>
			</Modal.Actions>
			{viewerDoc && (
				<DocViewer
					doc={viewerDoc}
					onClose={() => {
						return setViewerDoc(null);
					}}
				/>
			)}
		</Modal>
	);
};

FlowFormResponseModal.propTypes = {
	formName: PropTypes.string,
	flowId: PropTypes.string,
	fields: PropTypes.array,
	documents: PropTypes.array,
	onClose: PropTypes.func,
};

const FlowFormResponseCard = ({ formName, flowId, fields, documents }) => {
	const [open, setOpen] = useState(false);
	const preview = (fields || []).slice(0, PREVIEW_FIELDS);
	const extraCount = Math.max(0, (fields || []).length - PREVIEW_FIELDS);

	const handlePrint = () => {
		openPrintWindow(formName, flowId, fields, documents);
	};

	return (
		<div className={styles.card}>
			{formName && (
				<div className={styles.cardHeader}>
					<span className={styles.cardTitle}>{formName}</span>
				</div>
			)}
			{preview.length > 0 && (
				<div className={styles.cardFields}>
					{preview.map((field, i) => {
						return (
							<div key={i} className={styles.cardFieldRow}>
								<span className={styles.cardFieldName}>{getFieldLabel(field)}</span>
								<span className={styles.cardFieldValue}>{formatFieldValue(field) || "No especificado"}</span>
							</div>
						);
					})}
				</div>
			)}
			{extraCount > 0 && (
				<div className={styles.cardMore}>
					... {extraCount} {extraCount === 1 ? "campo más" : "campos más"}
				</div>
			)}
			<button
				type="button"
				className={styles.cardSeeBtn}
				onClick={(e) => {
					e.stopPropagation();
					setOpen(true);
				}}>
				<Icon name="eye" /> Ver formulario
			</button>
			<button type="button" className={styles.cardPrintBtn} onClick={handlePrint}>
				<Icon name="print" /> Imprimir
			</button>
			{open && (
				<FlowFormResponseModal
					formName={formName}
					flowId={flowId}
					fields={fields}
					documents={documents}
					onClose={() => {
						return setOpen(false);
					}}
				/>
			)}
		</div>
	);
};

FlowFormResponseCard.propTypes = {
	formName: PropTypes.string,
	flowId: PropTypes.string,
	fields: PropTypes.array,
	documents: PropTypes.array,
};

export default FlowFormResponseCard;
